import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Alert,
  Avatar,
  Tooltip,
  TablePagination,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AssignmentInd as AssignmentIndIcon, // for PAN Profile
} from "@mui/icons-material";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import * as XLSX from "xlsx";
// ------------------------
// URL + certificate helpers
// ------------------------
const looksLikeUrl = (s) => typeof s === "string" && /^https?:\/\//i.test(s);

const deepFindCertificateUrl = (input) => {
  const seen = new WeakSet();
  const visit = (node) => {
    if (!node || typeof node !== "object") return null;
    if (seen.has(node)) return null;
    seen.add(node);

    for (const k of Object.keys(node)) {
      const lk = k.toLowerCase();
      if (
        lk === "pdf_url" ||
        lk === "pdfurl" ||
        lk === "certificate_url" ||
        lk === "certificateurl" ||
        lk === "url" ||
        lk.endsWith("_url")
      ) {
        const v = node[k];
        if (looksLikeUrl(v)) return v;
      }
    }
    for (const v of Object.values(node)) {
      if (looksLikeUrl(v)) return v;
      if (v && typeof v === "object") {
        const found = visit(v);
        if (found) return found;
      }
    }
    return null;
  };
  return visit(input);
};

const toCloudFrontUrl = (input) => {
  if (!input) return "";
  const cdnBase = (process.env.REACT_APP_CLOUDFRONT_URL || "").replace(/\/+$|^\s+|\s+$/g, "");
  if (!cdnBase) return input;

  try {
    const inUrl = new URL(input);
    const cf = new URL(cdnBase);
    if (inUrl.host.toLowerCase() === cf.host.toLowerCase()) return input;

    let key = inUrl.pathname.replace(/^\/+/, "");
    const host = inUrl.host.toLowerCase();

    const s3Hosts = new Set([
      "s3.amazonaws.com",
      "s3.ap-south-1.amazonaws.com",
      "teckost.s3.amazonaws.com",
      "teckost.s3.ap-south-1.amazonaws.com",
    ]);
    if (s3Hosts.has(host) && key.startsWith("teckost/")) {
      key = key.slice("teckost/".length);
    }
    return `${cdnBase}/${key}`;
  } catch {
    return `${cdnBase}/${String(input).replace(/^\/+/, "")}`;
  }
};

const getCertificateUrl = (item) => {
  const dWrap = item?.["MSME Details"] ?? item ?? {};
  const d = dWrap?.result || dWrap || {};
  const fast =
    item?.certificate_url ||
    item?.certificateUrl ||
    d?.pdf_url ||
    d?.pdfUrl ||
    dWrap?.pdf_url ||
    dWrap?.pdfUrl;

  const url = fast && looksLikeUrl(fast) ? fast : deepFindCertificateUrl(item);
  return toCloudFrontUrl(url || "");
};

// ------------------------
// CIN State helpers
// ------------------------
const INDIA_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry"
];
const STATE_RE = new RegExp(`\\b(${INDIA_STATES.map(s=>s.replace(/[.*+?^${}()|[\\]\\\\]/g,"\\$&")).join("|")})\\b`, "i");

const getFirstMcaResult = (raw) => {
  try {
    const res = raw?.result;
    if (Array.isArray(res) && res.length) return res[0];
  } catch {}
  return null;
};

const rocToState = (roc) => {
  const map = {
    "roc ahmedabad": "Gujarat",
    "roc bangalore": "Karnataka",
    "roc bengaluru": "Karnataka",
    "roc chennai": "Tamil Nadu",
    "roc coimbatore": "Tamil Nadu",
    "roc delhi": "Delhi",
    "roc ernakulam": "Kerala",
    "roc gwalior": "Madhya Pradesh",
    "roc hyderabad": "Telangana",
    "roc jaipur": "Rajasthan",
    "roc jammu": "Jammu and Kashmir",
    "roc kanpur": "Uttar Pradesh",
    "roc kolkata": "West Bengal",
    "roc mumbai": "Maharashtra",
    "roc pune": "Maharashtra",
    "roc patna": "Bihar",
    "roc shillong": "Meghalaya",
    "roc vijayawada": "Andhra Pradesh",
    "roc chhattisgarh": "Chhattisgarh",
  };
  return map[String(roc || "").toLowerCase()] || null;
};

const resolveCinState = (r) => {
  const direct = r?.state || r?.location || r?.State || r?.State_Location;
  if (direct) return String(direct).trim();

  const mca = r?.mca_raw || r?.raw || r?.response;
  const first = getFirstMcaResult(mca);

  const abState = first?.addressBucket?.state;
  if (abState) return String(abState).trim();

  const reg = first?.registeredAddress;
  if (typeof reg === "string" && reg) {
    const m = reg.match(STATE_RE);
    if (m && m[1]) return m[1].trim();
  }

  const roc = first?.rocCode;
  const guess = rocToState(roc);
  if (guess) return guess;

  return "-";
};

// ------------------------
// Component
// ------------------------

export default function PanToCinUploader() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination for MSME
  const [msmePage, setMsmePage] = useState(0);
  const [msmeRowsPerPage, setMsmeRowsPerPage] = useState(10);

  // Pagination for CIN
  const [cinPage, setCinPage] = useState(0);
  const [cinRowsPerPage, setCinRowsPerPage] = useState(10);

  // Pagination for PAN Profile (Sheet3)
  const [panPage, setPanPage] = useState(0);
  const [panRowsPerPage, setPanRowsPerPage] = useState(10);

  // --- helpers ---

  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replaceAll('"', '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const display = (v) => (v === null || v === undefined || v === "" ? "-" : v);



  const downloadMsmeCertificate = (row) => {
    const url = toCloudFrontUrl(row?.__cert_link);
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  };


  // --- MSME rows ---

  const msmeRows = useMemo(() => {
    const source = resp?.msme_results || [];
    return source.map((item) => {
      const dWrap = item["MSME Details"] ?? item;
      const classificationTable = item["Classification Table"] || [];
      const currentClassification =
        Array.isArray(classificationTable) && classificationTable.length > 0

          ? classificationTable.find((r) => /^\d{4}-\d{2}$/.test(r?.["Classification Year"] || "")) || {}

          : {};
      const d = dWrap?.result || dWrap || {};
      const profile = d.profile || {};
      const official = d.officialAddress || {};
      const ind0 = Array.isArray(d.industry) && d.industry.length ? d.industry[0] : {};

      const joinAddr = (addr) => {
        const parts = [
          addr?.flat, addr?.premises, addr?.village, addr?.block, addr?.road,
          addr?.city, addr?.district, addr?.state, addr?.pincode,
        ].filter(Boolean);
        return parts.length ? parts.join(", ") : "-";
      };

      const certLink = getCertificateUrl(item);
      const hasUdyam = !!d.udyamRegistrationNo;
      const hasClassification = Array.isArray(classificationTable) && classificationTable.length > 0;
      const isMsmeFound = hasUdyam || hasClassification || !!certLink;

      return {
        PAN: display(item.PAN || profile.pan || d.pan || item["Taxpayer ID"]),
        MSME_RegistrationNo: display(d.udyamRegistrationNo),
        Merchant_name: display(d.enterpriseName || profile.name || item["Supplier Name"]),
        OfficialAddress: display(joinAddr(official)),
        enterpriseType: display(profile.enterpriseType),
        currentClassificationYear: display(currentClassification["Classification Year"]),
        dateOfRegistration: display(d.dateOfRegistration),
        Industry: display(ind0.industry),
        subSector: display(ind0.subSector),
        Gender: display(profile.gender),
        speciallyAbled: display(profile.speciallyAbled),
        certificate: certLink ? "Available" : isMsmeFound ? "Available" : "No data found",
        __raw_details: dWrap,
        __cert_link: certLink,
      };
    });
  }, [resp]);

  // CIN rows
  const cinRows = useMemo(() => {
    const source = resp?.cin_results || [];
    return source.map((r) => ({
      company_CIN_No:
        r.company_cin || r.company_CIN || r.company_CIN_No || r.company_cin_no || "-",
      company_name: r.company_name || r.company || r.name || "-",
      PAN: r.pan || r.PAN || "-",
      State: resolveCinState(r),
      __raw: r,
    }));
  }, [resp]);


  // --- PAN Profile rows (Sheet3 only) ---
  const panProfileRows = useMemo(() => {
    const source = resp?.pan_profile_results || [];
    return source.map((r) => ({
      PAN: r.PAN || r.pan || "-",
      Status: r.profile_status || r.status || r.result?.status || "-",
      Name: r.name || r.result?.name || "-",
      DOB: r.dob || r.result?.dob || "-",
      __raw: r,
    }));
  }, [resp]);

  const panProfileSummary = useMemo(() => {
    const total = panProfileRows.length;
    const ok = panProfileRows.filter((r) => r.Status && r.Status !== "-" && r.Status !== "invalid_pan" && r.Status !== "perfios_error").length;
    return { total, ok };
  }, [panProfileRows]);

  // --- exports ---

  const exportMsmeCsv = () => {
    const wb = XLSX.utils.book_new();
    const header = [
      "PAN Number","MSME RegistrationNo","Merchant name","OfficialAddress",
      "enterpriseType","classificationYear","dateOfRegistration","Industry",
      "subSector","Gender","speciallyAbled","certificate","certificate_link",
    ];
    const msmeData = [header];
    const msmeLinks = [];
    const nonMsmeData = [header];
    const nonMsmeLinks = [];
    msmeRows.forEach((r) => {
      const row = [
        r.PAN ?? "", r.MSME_RegistrationNo ?? "", r.Merchant_name ?? "", r.OfficialAddress ?? "",
        r.enterpriseType ?? "", r.currentClassificationYear ?? "", r.dateOfRegistration ?? "",
        r.Industry ?? "", r.subSector ?? "", r.Gender ?? "", r.speciallyAbled ?? "",
        r.certificate ?? "", "Download",
      ];
      const hasMsme = r.MSME_RegistrationNo !== "-" || r.__cert_link;
      if (hasMsme) { msmeData.push(row); msmeLinks.push(r.__cert_link); }
      else { nonMsmeData.push(row); nonMsmeLinks.push(r.__cert_link); }
    });
    const buildSheet = (data, links, sheetName) => {
      const ws = XLSX.utils.aoa_to_sheet(data);

      ws["!cols"] = [
        { wch: 14 }, { wch: 22 }, { wch: 28 }, { wch: 40 }, { wch: 16 },
        { wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 28 }, { wch: 10 },
        { wch: 16 }, { wch: 14 }, { wch: 18 },
      ];
      const headerRow = data[0];
      const linkColIndex = headerRow.indexOf("certificate_link");

      for (let i = 1; i < data.length; i++) {
        const cellRef = XLSX.utils.encode_cell({ r: i, c: linkColIndex });
        let url = links[i - 1] || "";
        if (!url && msmeRows[i - 1]?.__raw_details) {
          url = getCertificateUrl(msmeRows[i - 1].__raw_details);
        }
        if (url && ws[cellRef]) {
          ws[cellRef].l = { Target: url, Tooltip: "Download certificate" };
          ws[cellRef].t = "s";
          ws[cellRef].v = "Download";
        } else if (ws[cellRef]) {
          ws[cellRef].t = "s";
          ws[cellRef].v = "-";
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };
    if (msmeData.length > 1) buildSheet(msmeData, msmeLinks, "MSME Found");
    if (nonMsmeData.length > 1) buildSheet(nonMsmeData, nonMsmeLinks, "MSME Not Found");
    XLSX.writeFile(wb, "msme_results.xlsx");
  };



  const exportCinCsv = () => {
    const headers = ["company_CIN No", "company_name", "PAN", "State"];
    const lines = [headers.join(",")];
    cinRows.forEach((r) => {
      const row = [r.company_CIN_No, r.company_name, r.PAN, r.State].map(esc).join(",");
      lines.push(row);
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cin_results_selected.csv";
    a.click();
  };


  // NEW: export PAN Profile CSV
  const exportPanProfileCsv = () => {
    const headers = ["PAN", "Status", "Name", "DOB"];
    const lines = [headers.join(",")];
    panProfileRows.forEach((r) => {
      const row = [r.PAN, r.Status, r.Name, r.DOB].map(esc).join(",");
      lines.push(row);
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pan_profile_basic_results.csv";
    a.click();
  };

  // --- upload handler ---

  const onUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API_ENDPOINT.bulk_pan_to_msme_and_cin}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResp(res.data);
      setPanPage(0); // reset PAN profile paginator
    } catch (err) {
      console.error("Upload error:", err);
      setError(err?.response?.data?.message || "Failed to process file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };


  // pagination handlers

  const handleMsmePageChange = (event, newPage) => setMsmePage(newPage);
  const handleMsmeRowsPerPageChange = (event) => {
    setMsmeRowsPerPage(parseInt(event.target.value, 10));
    setMsmePage(0);
  };
  const handleCinPageChange = (event, newPage) => setCinPage(newPage);
  const handleCinRowsPerPageChange = (event) => {
    setCinRowsPerPage(parseInt(event.target.value, 10));
    setCinPage(0);
  };
  const handlePanPageChange = (event, newPage) => setPanPage(newPage);
  const handlePanRowsPerPageChange = (event) => {
    setPanRowsPerPage(parseInt(event.target.value, 10));
    setPanPage(0);
  };


  return (
    <div style={{ margin: "1rem" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>

        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          PAN-based Supplier Compliance Verification

        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: "12px" }}>
          Upload a file containing PAN numbers to retrieve corresponding CIN, MSME, and PAN Profile details
        </Typography>
      </Box>

      {/* Upload Section */}
      <Card elevation={3} sx={{ mb: 4, borderRadius: 3, p: 3 }}>
        <Stack spacing={3} alignItems="center">
          <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
            <UploadFileIcon fontSize="large" />
          </Avatar>
          <Typography variant="h6" fontWeight="600">
            Upload PAN Data File
          </Typography>
          <Typography variant="h6" color="text.secondary" align="center">
            Select an Excel/CSV file from your system and click upload to begin processing.
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv, .xlsx, .xls"
            style={{ display: "none" }}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()} startIcon={<UploadFileIcon />}>
              Choose File
            </Button>
            <Button variant="contained" onClick={onUpload} startIcon={<CloudUploadIcon />} disabled={!file || loading}>
              {loading ? "Processing..." : "Upload"}
            </Button>
          </Stack>
          {file && (
            <Chip icon={<DescriptionIcon />} label={file.name} onDelete={clearFile} sx={{ mt: 2, fontSize: "12px" }} />
          )}
        </Stack>
      </Card>

      {loading && <LinearProgress sx={{ mb: 4 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results summary */}
      {resp && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            Results Generated Successfully
          </Typography>


          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>

            <Chip
              icon={<BusinessIcon />}
              label={`${msmeRows.filter((r) => r.MSME_RegistrationNo !== "-").length} MSME Records`}
              color="primary"
              variant="outlined"
              sx={{ fontSize: "14px" }}
            />
            <Chip
              icon={<BadgeIcon />}
              label={`${cinRows.length} CIN Records`}
              color="secondary"
              variant="outlined"
              sx={{ fontSize: "14px" }}
            />
            {/* NEW: PAN Profile summary (Sheet3) */}
            <Chip
              icon={<AssignmentIndIcon />}
              label={`${panProfileSummary.ok}/${panProfileSummary.total} PAN Profiles`}
              color="success"
              variant="outlined"
              sx={{ fontSize: "14px" }}
            />
          </Box>
        </Box>
      )}

      {/* MSME Table */}
      <Card elevation={3} sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="600">
              MSME Registration Details
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportMsmeCsv} disabled={!msmeRows.length}>
              Export CSV
            </Button>
          </Stack>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "grey.100" }}>
                <TableRow>
                  {[
                    "PAN","MSME No.","Merchant Name","Enterprise Type","Classification Year",
                    "Industry","Gender","Certificate","Action",
                  ].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: "12px" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {msmeRows.length ? (
                  msmeRows
                    .slice(msmePage * msmeRowsPerPage, msmePage * msmeRowsPerPage + msmeRowsPerPage)
                    .map((row, idx) => (

                      <TableRow
                        key={`msme-${msmePage}-${idx}`}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "12px" }}>{row.PAN}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          <Typography variant="body2" fontWeight={row.MSME_RegistrationNo !== "-" ? "medium" : "normal"} sx={{ fontSize: "12px" }}>
                            {row.MSME_RegistrationNo}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{row.Merchant_name}</TableCell>
                        <TableCell>
                          <Chip label={row.enterpriseType} size="small" color="primary" variant="outlined" sx={{ fontSize: "12px" }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={row.currentClassificationYear} size="small" color="info" variant="outlined" sx={{ fontSize: "12px" }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{row.Industry}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.Gender}
                            size="small"
                            color={row.Gender === "Female" ? "secondary" : row.Gender === "Male" ? "primary" : "default"}
                            variant="outlined"
                            sx={{ fontSize: "12px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={row.__cert_link ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={row.__cert_link ? "Available" : "No data found"}
                            size="small"
                            color={row.__cert_link ? "success" : "error"}
                            variant="outlined"
                            sx={{ fontSize: "12px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Download Certificate">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => downloadMsmeCertificate(row)}
                                disabled={!row.__cert_link}
                                startIcon={<DownloadIcon />}
                                sx={{ textTransform: "none", fontSize: "15px" }}
                              >
                                PDF
                              </Button>
                            </span>
                          </Tooltip>

                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>

                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary", fontSize: "15px" }}>

                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <DescriptionIcon sx={{ fontSize: 56, mb: 1, color: "action.disabled" }} />
                        <Typography variant="h6">No MSME data available</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Upload a file to view MSME registration details
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={msmeRows.length}
              rowsPerPage={msmeRowsPerPage}
              page={msmePage}
              onPageChange={handleMsmePageChange}
              onRowsPerPageChange={handleMsmeRowsPerPageChange}
            />
          </TableContainer>
        </CardContent>
      </Card>

      {/* CIN Table */}
      <Card elevation={3} sx={{ borderRadius: 3, mb: 4 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Company CIN Details
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportCinCsv} disabled={!cinRows.length} sx={{ textTransform: "none" }}>
              Export CSV
            </Button>
          </Stack>

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "grey.100" }}>
                <TableRow>
                  {["CIN No.", "Company Name", "PAN", "State"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {cinRows.length ? (
                  cinRows
                    .slice(cinPage * cinRowsPerPage, cinPage * cinRowsPerPage + cinRowsPerPage)
                    .map((row, idx) => (
                      <TableRow
                        key={`cin-${idx}`}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium" sx={{ fontSize: "14px" }}>
                            {row.company_CIN_No}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>{row.company_name}</TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>
                          <Chip label={row.PAN} size="small" variant="outlined" color="primary" sx={{ fontSize: "14px" }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>{row.State}</TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <DescriptionIcon sx={{ fontSize: 56, mb: 1, color: "action.disabled" }} />
                        <Typography variant="h6" fontWeight={500}>
                          No CIN data available
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                          Upload a file to view CIN registration details
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={cinRows.length}
              rowsPerPage={cinRowsPerPage}
              page={cinPage}
              onPageChange={handleCinPageChange}
              onRowsPerPageChange={handleCinRowsPerPageChange}
            />

          </TableContainer>
        </CardContent>
      </Card>

      {/* NEW: PAN Profile Basic (Sheet3) */}
      <Card elevation={3} sx={{ borderRadius: 3, mb: 4 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              PAN Profile Basic (Sheet3)
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportPanProfileCsv}
              disabled={!panProfileRows.length}
              sx={{ textTransform: "none" }}
            >
              Export CSV
            </Button>
          </Stack>

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "grey.100" }}>
                <TableRow>
                  {["PAN", "Status", "Name", "DOB"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {panProfileRows.length ? (
                  panProfileRows
                    .slice(panPage * panRowsPerPage, panPage * panRowsPerPage + panRowsPerPage)
                    .map((row, idx) => (
                      <TableRow
                        key={`panprof-${idx}`}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "14px" }}>
                          <Chip label={row.PAN || "-"} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>
                          <Chip
                            icon={<AssignmentIndIcon />}
                            label={row.Status || "-"}
                            size="small"
                            color={row.Status?.toLowerCase() === "active" ? "success" : row.Status?.toLowerCase() === "inactive" ? "default" : "info"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>{row.Name}</TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>{row.DOB}</TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <DescriptionIcon sx={{ fontSize: 56, mb: 1, color: "action.disabled" }} />
                        <Typography variant="h6" fontWeight={500}>
                          No PAN profile data
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                          Ensure Sheet3 contains PANs for PAN Profile Basic lookups
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={panProfileRows.length}
              rowsPerPage={panRowsPerPage}
              page={panPage}
              onPageChange={handlePanPageChange}
              onRowsPerPageChange={handlePanRowsPerPageChange}
            />
          </TableContainer>
        </CardContent>
      </Card>
    </div>
  );
}
